import React, { useMemo, useState } from 'react';
import { sanitizeInput } from '@core/utils/dompurify/dompurify.js';
import { formatPhoneNumberIntl, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { Admin } from 'FielderUtils/roles/roles';
import SelectJobPosition from '../UsersSectionComponents/SelectJobPosition';
import ErrorCodes from 'FielderUtils/ErrorCodes';
import { FIELDER } from '@core/constants/base.constants';
import {
  validateUsername,
  validateName,
  validateLastname,
  validateSecondEmail,
  validateEmail,
} from '@core/utils/validations/FieldValidations';
import { useDispatch, useSelector } from 'react-redux';
import { loadingStart, loadingStop } from '@core/store/modules/loading/loading.slice';
import { createAdministratorRequest } from '@core/api/administrator/administrator.service';
import TextField from '~/TextField/TextField.component';
import { FielderPhoneField } from '~/FielderElements/FielderElements.component';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const { txt: TXT_ID, btn: BTN_ID } = FIELDER.idElement;
const AddAdministrator = ({ lan, setDefaultFalseAdd, errorMessageCreation }) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.utils.session);
  const theme = useTheme();
  const [userInfo, setUserInfo] = useState({
    username: '',
    name: '',
    lastName: '',
    email: '',
    emailConfirm: '',
    officePhone: '',
    jobPosition: '',
    otherJobPosition: '',
  });
  const [errors, setErrors] = useState({
    username: false,
    name: false,
    lastName: false,
    email: false,
    emailConfirmation: false,
    officePhone: false,
    cellphone: false,
  });
  const [activateAlertUserName, setActivateAlertUserName] = useState(false);
  const [phoneNumberValue, setPhoneNumberValue] = useState('');
  const [countryIsoName, setCountryIsoName] = useState(null);
  const [countryCallingCode, setCountryCallingCode] = useState(undefined);
  const [errorOfficePhone, setErrorOfficePhone] = useState('');
  const [errorMobilePhone, setErrorMobilePhone] = useState('');
  const onChangeField = (event) => {
    if (!event) return;
    const NUMBER_INPUTS = new Set([TXT_ID.phone, TXT_ID.mobile]);
    if (NUMBER_INPUTS.has(event.target.id)) {
      const re = /^[0-9\b]+$/;
      if (!re.test(event.target.value)) return;
    }
    const value = sanitizeInput(event.target.value);
    const name = event.target.name;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };
  const handleChangeJobPosition = (value) => {
    setUserInfo((prev) => ({ ...prev, jobPosition: value }));
  };
  const validateCellPhone = (cellphone) => {
    if (cellphone.length > 0 && isValidPhoneNumber(cellphone)) return true;
    setErrorMobilePhone(lan.messageErrorPhone);
    return false;
  };
  const validateOfficePhone = (officePhone) => {
    if (officePhone.length > 0) {
      if (isValidPhoneNumber(officePhone)) return true;
      setErrorOfficePhone(lan.messageErrorPhone);
      return false;
    }
    return true;
  };
  const getUserNameHelperText = useMemo(() => {
    if (activateAlertUserName) return lan.userNameValidation;
    if (errors.username) return lan.userNameFieldHelp;
    return null;
  }, [activateAlertUserName, errors.username, lan]);
  const createAdmin = () => {
    const jobPosition =
      userInfo.jobPosition !== lan.other ? userInfo.jobPosition : userInfo.otherJobPosition;
    const body = {
      clientUser: {
        idAccount: session.account.id,
        idUser: session.user.idUser,
        userName: userInfo.username,
        name: userInfo.name,
        lastName: userInfo.lastName,
        email: userInfo.email,
        phoneNumber: formatPhoneNumberIntl(userInfo.officePhone),
        status: 0,
        mobileNumber: formatPhoneNumberIntl(phoneNumberValue),
        jobPosition,
        role: Admin.name,
        language: session.user.language,
      },
      isoName: countryIsoName,
    };

    const onSuccess = () => setDefaultFalseAdd();

    const onError = ({ status }) => {
      if (status == ErrorCodes.FAIL_DUPLICATE_COGNITO_USER || status == ErrorCodes.DUPLICATED_USERNAME) {
        setActivateAlertUserName(true);
        setErrors((prev) => ({ ...prev, username: true }));
      } else {
        errorMessageCreation();
      }
    };

    const loadingConfig = { name: 'CREATE_ADMINISTRATOR', dispatch };
    createAdministratorRequest(body, onSuccess, onError, loadingConfig);
  };
  const handleDoActionAddAdmin = () => {
    dispatch(loadingStart({ name: 'CREATE_ADMINISTRATOR' }));

    const {
      username, name, lastName, email, emailConfirm, officePhone,
    } = userInfo;

    const isValidUserName = validateUsername(username);
    const isValidName = validateName(name);
    const isValidLastName = validateLastname(lastName);
    const isValidEmail = validateEmail(email);
    const isValidSecondEmail = validateSecondEmail(email, emailConfirm);
    const isValidOfficePhone = validateOfficePhone(officePhone);
    const isValidCellPhone = validateCellPhone(phoneNumberValue);

    const nextErrors = {
      username: !isValidUserName,
      name: !isValidName,
      lastName: !isValidLastName,
      email: !isValidEmail,
      emailConfirmation: !isValidSecondEmail,
      officePhone: !isValidOfficePhone,
      cellphone: !isValidCellPhone,
    };
    setActivateAlertUserName(false);
    setErrors(nextErrors);

    if (
      isValidUserName &&
      isValidName &&
      isValidLastName &&
      isValidEmail &&
      isValidSecondEmail &&
      isValidCellPhone &&
      isValidOfficePhone
    ) {
      createAdmin();
    } else {
      dispatch(loadingStop({ name: 'CREATE_ADMINISTRATOR' }));
    }
  };
  const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
      {children}
    </Typography>
  );
  return (
    <Box
      sx={{
        p: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Grid container spacing={3}>
        {/* LEFT SIDE */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2.25}>
            <TextField
              id={TXT_ID.username}
              name="username"
              label={`${lan.userName}*`}
              value={userInfo.username}
              onChange={onChangeField}
              error={Boolean(errors.username)}
              helperText={getUserNameHelperText}
              maxSize={150}
              fullWidth
            />
            <TextField
              id={TXT_ID.name}
              name="name"
              value={userInfo.name}
              label={`${lan.name}*`}
              onChange={onChangeField}
              error={Boolean(errors.name)}
              helperText={errors.name ? lan.nameFieldHelp : null}
              fullWidth
            />
            <TextField
              id={TXT_ID.lastName}
              name="lastName"
              label={`${lan.lastName}*`}
              value={userInfo.lastName}
              onChange={onChangeField}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName ? lan.lastNameFieldHelp : null}
              fullWidth
            />
            <TextField
              id={TXT_ID.email}
              name="email"
              type="email"
              label={`${lan.email}*`}
              value={userInfo.email}
              onChange={onChangeField}
              error={Boolean(errors.email)}
              helperText={errors.email ? lan.emailFieldHelp : null}
              fullWidth
            />
            <TextField
              id={TXT_ID.emailConfirm}
              name="emailConfirm"
              type="email"
              label={`${lan.confirmEmail}*`}
              value={userInfo.emailConfirm}
              onChange={onChangeField}
              error={Boolean(errors.emailConfirmation)}
              helperText={errors.emailConfirmation ? lan.validateSameEmail : null}
              fullWidth
            />
          </Stack>
        </Grid>
        {/* RIGHT SIDE */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            elevation={1}
            sx={{
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <CardContent sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
              <Stack spacing={2.25}>
                <Box>
                  <SectionTitle>{lan.jobPosition}</SectionTitle>
                  <SelectJobPosition
                    lan={lan}
                    onChange={handleChangeJobPosition}
                    defaultValue={'0'}
                    disabled={false}
                  />
                  {userInfo.jobPosition === lan.other && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        id={TXT_ID.jobPosition}
                        name="otherJobPosition"
                        label={lan.anotherJob}
                        value={userInfo.otherJobPosition}
                        onChange={onChangeField}
                        size="small"
                        fullWidth
                      />
                    </Box>
                  )}
                </Box>
                <FielderPhoneField
                  id={TXT_ID.phone}
                  label={lan.officePhone}
                  value={userInfo.officePhone}
                  onChange={(value) => {
                    const v = value === undefined ? '' : value;
                    setErrorOfficePhone('');
                    setUserInfo((prev) => ({ ...prev, officePhone: v }));
                  }}
                  lan={lan}
                  isRequired={false}
                  externalError={errorOfficePhone}
                />
                <FielderPhoneField
                  id={TXT_ID.mobile}
                  label={lan.mobilePhone}
                  value={phoneNumberValue}
                  onChange={(value) => {
                    const valueNumber = value || '';
                    const formattedPhone = formatPhoneNumberIntl(valueNumber);
                    let parsedNumber;
                    try {
                      parsedNumber = parsePhoneNumber(valueNumber);
                    } catch (err) {
                    }
                    setErrorMobilePhone('');
                    setPhoneNumberValue(formattedPhone);
                    setCountryIsoName(parsedNumber?.country);
                    setCountryCallingCode(parsedNumber?.countryCallingCode);
                  }}
                  lan={lan}
                  isRequired
                  externalError={errorMobilePhone}
                />
                {(errors.username ||
                  errors.name ||
                  errors.lastName ||
                  errors.email ||
                  errors.emailConfirmation ||
                  errors.cellphone ||
                  errors.officePhone) && (
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      color: theme.palette.primary.main,
                      mt: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    *{lan.requiredFields}
                  </Typography>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                  <Button
                    id={BTN_ID.create}
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleDoActionAddAdmin}
                    sx={{
                      px: 3.5,
                      height: 48,
                      borderRadius: 1.25,
                    }}
                  >
                    {lan.add}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
export default AddAdministrator;
//Original code:
/*import React, { Component } from 'react';
import { sanitizeInput } from '@core/utils/dompurify/dompurify.js';
import { formatPhoneNumberIntl, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { Admin } from 'FielderUtils/roles/roles';
import SelectJobPosition from '../UsersSectionComponents/SelectJobPosition';
import ErrorCodes from 'FielderUtils/ErrorCodes';
import { FIELDER } from '@core/constants/base.constants';
import {
  validateUsername,
  validateName,
  validateLastname,
  validateSecondEmail,
  validateEmail,
} from '@core/utils/validations/FieldValidations';
import { connect } from 'react-redux';
import 'rc-time-picker/assets/index.css';
import { loadingStart, loadingStop } from '@core/store/modules/loading/loading.slice';
import { createAdministratorRequest } from '@core/api/administrator/administrator.service';
import TextField from '~/TextField/TextField.component';
import { FielderPhoneField } from '~/FielderElements/FielderElements.component';
import Grid from '@mui/material/Grid2';
import { Card, CardContent } from '@mui/material';

const { txt: TXT_ID, btn: BTN_ID } = FIELDER.idElement;
class AddAdministrator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userInfo: {
        username: '',
        name: '',
        lastName: '',
        email: '',
        officePhone: '',
        jobPosition: '',
        otherJobPosition: '',
      },
      errors: {
        username: null,
        name: null,
        lastName: null,
        email: null,
        officePhone: null,
      },
      disabledInput: true,
      activateAlert: false,
      activateAlertUserName: false,
      phoneNumberValue: '',
      countryIsoName: null,
      countryCallingCode: undefined,
      showModalAddAdministrator: props.showModalAddAdministrator,
      validateSameEmail: true,
      sameEmail: false,
      errorOfficePhone: '',
      errorMobilePhone: '',
    };
  }
  onChangeField = (event) => {
    if (!event) return;

    const NUMBER_INPUTS = new Set([TXT_ID.phone, TXT_ID.mobile]);
    if (NUMBER_INPUTS.has(event.target.id)) {
      const re = /^[0-9\b]+$/;
      if (!re.test(event.target.value)) return;
    }

    const sanitizedInput = sanitizeInput(event.target.value);
    this.setState((s) => ({ ...s, userInfo: { ...s.userInfo, [event.target.name]: sanitizedInput } }));
  };
  handleChangeJobPosition = (value) => {
    this.setState((s) => ({ ...s, userInfo: { ...s.userInfo, jobPosition: value } }));
  };
  validateCellPhone = (cellphone) => {
    if (cellphone.length > 0 && isValidPhoneNumber(cellphone)) return true;
    this.setState({ errorMobilePhone: this.props.lan.messageErrorPhone });
    return false;
  };
  validateOfficePhone = (officePhone) => {
    if (officePhone.length > 0) {
      if (isValidPhoneNumber(officePhone)) return true;
      this.setState({ errorOfficePhone: this.props.lan.messageErrorPhone });
      return false;
    }
    return true;
  };
  handleDoActionAddAdmin = () => {
    this.props.dispatch(loadingStart({ name: 'CREATE_ADMINISTRATOR' }));

    const secondEmail = document.getElementById(TXT_ID.emailConfirm).value;
    const isValidUserName = validateUsername(this.state.userInfo.username);
    const isValidName = validateName(this.state.userInfo.name);
    const isValidLastName = validateLastname(this.state.userInfo.lastName);
    const isValidEmail = validateEmail(this.state.userInfo.email);
    const isValidSecondEmail = validateSecondEmail(this.state.userInfo.email, secondEmail);
    const isValidOfficePhone = this.validateOfficePhone(this.state.userInfo.officePhone);
    const isValidCellPhone = this.validateCellPhone(this.state.phoneNumberValue);

    const errors = {
      username: !isValidUserName,
      name: !isValidName,
      lastName: !isValidLastName,
      email: !isValidEmail,
      emailConfirmation: !isValidSecondEmail,
      officePhone: !isValidOfficePhone,
      cellphone: !isValidCellPhone,
    };
    this.setState({ activateAlertUserName: false, errors });

    if (isValidUserName && isValidName && isValidLastName && isValidEmail && isValidSecondEmail && isValidCellPhone && isValidOfficePhone) {
      document.getElementById('register-form-hint').style.display = 'none';
      this.createAdmin();
    } else {
      document.getElementById('register-form-hint').style.display = 'inline';
      this.props.dispatch(loadingStop({ name: 'CREATE_ADMINISTRATOR' }));
    }
  };
  createAdmin = () => {
    const jobPosition =
      this.state.userInfo.jobPosition !== this.props.lan.other
        ? this.state.userInfo.jobPosition
        : this.state.userInfo.otherJobPosition;

    const body = {
      clientUser: {
        idAccount: this.props.session.account.id,
        idUser: this.props.session.user.idUser,
        userName: this.state.userInfo.username,
        name: this.state.userInfo.name,
        lastName: this.state.userInfo.lastName,
        email: this.state.userInfo.email,
        phoneNumber: formatPhoneNumberIntl(this.state.userInfo.officePhone),
        status: 0,
        mobileNumber: formatPhoneNumberIntl(this.state.phoneNumberValue),
        jobPosition,
        role: Admin.name,
        language: this.props.session.user.language,
      },
      isoName: this.state.countryIsoName,
    };

    const onSuccess = () => this.props.setDefaultFalseAdd();

    const onError = ({ status }) => {
      if (status == ErrorCodes.FAIL_DUPLICATE_COGNITO_USER || status == ErrorCodes.DUPLICATED_USERNAME) {
        this.setState((s) => ({
          ...s,
          activateAlertUserName: true,
          errors: { ...s.errors, username: true },
        }));
      } else {
        this.props.errorMessageCreation();
      }
    };

    const loadingConfig = { name: 'CREATE_ADMINISTRATOR', dispatch: this.props.dispatch };
    createAdministratorRequest(body, onSuccess, onError, loadingConfig);
  };
  getUserNameHelperText = () => {
    if (this.state.activateAlertUserName) return this.props.lan.userNameValidation;
    if (this.state.errors.username) return this.props.lan.userNameFieldHelp;
    return null;
  };
  render() {
    return (
      <>
        <div className="Fielder-body p-3" style={{ backgroundColor: 'white' }}>
          <Grid container>
<Grid size={{ xs: 12, md: 6 }} className="px-4">
  <div className="row my-3">
    <div className="col-sm-12">
      <TextField
        id={TXT_ID.username}
        name="username"
        label={`${this.props.lan.userName}*`}
        value={this.state.userInfo.username}
        onChange={this.onChangeField}
        error={this.state.errors.username}
        helperText={this.getUserNameHelperText()}
        maxSize={150}
      />
    </div>
  </div>
  <div className="row my-3">
    <div className="col-sm-12">
      <TextField
        id={TXT_ID.name}
        name="name"
        value={this.state.userInfo.name}
        label={`${this.props.lan.name}*`}
        onChange={this.onChangeField}
        error={this.state.errors.name}
        helperText={this.state.errors.name && this.props.lan.nameFieldHelp}
      />
    </div>
  </div>
  <div className="row my-3">
    <div className="col-sm-12">
      <TextField
        id={TXT_ID.lastName}
        name="lastName"
        label={`${this.props.lan.lastName}*`}
        value={this.state.userInfo.lastName}
        onChange={this.onChangeField}
        error={this.state.errors.lastName}
        helperText={this.state.errors.lastName && this.props.lan.lastNameFieldHelp}
      />
    </div>
  </div>
  <div className="row my-3">
    <div className="col">
      <TextField
        id={TXT_ID.email}
        name="email"
        type="email"
        label={`${this.props.lan.email}*`}
        value={this.state.userInfo.email}
        onChange={this.onChangeField}
        error={this.state.errors.email}
        helperText={this.state.errors.email && this.props.lan.emailFieldHelp}
      />
    </div>
  </div>
  <div className="row my-3">
    <div className="col">
      <TextField
        id={TXT_ID.emailConfirm}
        name="emailConfirm"
        type="email"
        label={`${this.props.lan.confirmEmail}*`}
        value={this.state.userInfo.emailConfirm}
        onChange={this.onChangeField}
        error={this.state.errors.emailConfirmation}
        helperText={this.state.errors.emailConfirmation && this.props.lan.validateSameEmail}
      />
    </div>
  </div>
</Grid>
<Grid size={{ xs: 12, md: 6 }} className="px-4">
  <Card className="Fielder-cards-border">
    <CardContent className="mx-4">
      <div className="mb-4">
        <div className="col-sm-12 Fielder-cards-txt-label-input">{this.props.lan.jobPosition}</div>
        <div className="col-sm-12 ">
          <SelectJobPosition lan={this.props.lan} onChange={this.handleChangeJobPosition} defaultValue={'0'} disabled={false} />
        </div>
        {this.state.userInfo.jobPosition == this.props.lan.other && (
          <div className="col-sm-8 my-3">
            <TextField
              id={TXT_ID.jobPosition}
              name="otherJobPosition"
              label={this.props.lan.anotherJob}
              value={this.state.userInfo.otherJobPosition}
              onChange={this.onChangeField}
              size="small"
            />
          </div>
        )}
      </div>
      <div className="row my-3">
        <div className="col">
          <FielderPhoneField
            id={TXT_ID.phone}
            label={this.props.lan.officePhone}
            value={this.state.userInfo.officePhone}
            onChange={(value) => {
              this.setState((s) => ({
                ...s,
                errorOfficePhone: '',
                userInfo: { ...s.userInfo, officePhone: value === undefined ? '' : value },
              }));
            }}
            lan={this.props.lan}
            isRequired={false}
            externalError={this.state.errorOfficePhone}
          />
        </div>
      </div>
      <div className="row my-3">
        <div className="col">
          <FielderPhoneField
            id={TXT_ID.mobile}
            label={this.props.lan.mobilePhone}
            value={this.state.phoneNumberValue}
            onChange={(value) => {
              const valueNumber = value || '';
              const formattedPhone = formatPhoneNumberIntl(valueNumber);
              let parsedNumber;
              try {
                parsedNumber = parsePhoneNumber(valueNumber);
              } catch (err) {
                console.warn('Failed to parse phone number:', err);
              }
              this.setState((prev) => ({
                ...prev,
                errorMobilePhone: '',
                phoneNumberValue: formattedPhone,
                countryIsoName: parsedNumber?.country,
                countryCallingCode: parsedNumber?.countryCallingCode,
              }));
            }}
            lan={this.props.lan}
            isRequired
            externalError={this.state.errorMobilePhone}
          />
        </div>
      </div>

      <div id="register-form-hint" className="Register-form-hint mt-3">
        <p>*{this.props.lan.requiredFields}</p>
      </div>

      <div className="row my-3">
        <div className="col-sm-auto ml-auto">
          <button
            id={BTN_ID.create}
            type="submit"
            className="btn btn-outline-primary btn-success create-client-modal-btnsave-size"
            onClick={this.handleDoActionAddAdmin}
          >
            {this.props.lan.add}
          </button>
        </div>
      </div>
    </CardContent>
  </Card>
</Grid>
</Grid>
</div>
</>
);
}
}
const mapStateToProps = (state) => ({
  session: state.utils.session,
});
export default connect(mapStateToProps)(AddAdministrator);*/
